import { IntroRequestStatus } from "@shared/schemas/intro-request-state.schemas";
import {
  EventIdPublic,
  EventParticipantIdPublic,
  type ConnectionRequestIdPublic,
} from "@shared/schemas/branded-id.schemas";
import type { ConnectionRequestSummary } from "@shared/schemas/connection-request.schemas";
import type { DirectoryEntry } from "@shared/schemas/directory-entry.schemas";
import type { DirectoryCard } from "@frontend/shared/domain/types/directory-card.types";
import type { DirectoryProfile } from "@frontend/shared/domain/types/directory-profile.types";
import { resolvePhotoUrl } from "@shared/utils/participant-photo-url.utils";

class ParticipantNotFoundError extends Error {
  override readonly name = "ParticipantNotFoundError";
  constructor(participantIdPublic: EventParticipantIdPublic) {
    super(
      `Participant ${EventParticipantIdPublic.unwrap(participantIdPublic)} not found`,
    );
  }
}

export interface GetDirectoryProfileRawData {
  readonly participantDto: DirectoryEntry | undefined;
  readonly introsItems: readonly ConnectionRequestSummary[];
}

// Projects the served `DirectoryEntry` envelope down to the flat presentation
// card: display-name + AI flag come off the envelope; the rest are read off the
// omission-only view (`{ value }` projections). `resolvePhotoUrl` resolves and
// re-brands the raw photo-url value to `ParticipantPhotoUrl`. The envelope is
// already parsed at the repository boundary, so there is no re-parse here.
export function directoryEntryToCard(envelope: DirectoryEntry): DirectoryCard {
  const { entry, displayName, isBioAIGenerated } = envelope;

  return {
    participantIdPublic: entry.idPublic,
    userIdPublic: entry.userIdPublic,
    eventIdPublic: entry.eventIdPublic,
    displayName,
    lastName: entry.lastName.value,
    headline: entry.title?.value ?? null,
    company: entry.companyName,
    personalLinkedInUrl: entry.personalLinkedInUrl,
    photoUrl: resolvePhotoUrl(
      entry.photoUrl?.value,
      EventIdPublic.unwrap(entry.eventIdPublic),
    ),
    bio: entry.bio?.value ?? null,
    summary: entry.summary?.value ?? null,
    isBioAIGenerated,
  };
}

type ConnectionDirectionType = "from" | "to";

// Connected-profile cards come from a `ConnectionRequestSummary` (denormalized
// display strings only — no participant fields to omit-down), so they are built
// directly into the flat card rather than projected from an envelope.
export function connectionRequestSummaryToDirectoryCard(
  dto: ConnectionRequestSummary,
  direction: ConnectionDirectionType,
  eventIdPublic: EventIdPublic,
): DirectoryCard {
  const isFrom = direction === "from";
  return {
    participantIdPublic: isFrom
      ? dto.fromParticipantIdPublic
      : dto.toParticipantIdPublic,
    userIdPublic: null,
    eventIdPublic,
    displayName: isFrom ? dto.fromUserName : dto.toUserName,
    // The summary denormalizes the name to a single display string, so the
    // separate sort key never reaches this builder.
    lastName: null,
    headline: isFrom ? dto.fromUserHeadline : dto.toUserHeadline,
    company: isFrom ? dto.fromUserCompany : dto.toUserCompany,
    personalLinkedInUrl: null,
    photoUrl: resolvePhotoUrl(
      isFrom ? dto.fromUserPhotoUrl : dto.toUserPhotoUrl,
      EventIdPublic.unwrap(eventIdPublic),
    ),
    bio: null,
    summary: null,
    isBioAIGenerated: false,
  };
}

export function resolveConnectionRequestSummaryPhotoUrls(
  summary: ConnectionRequestSummary,
  eventIdPublic: string,
): ConnectionRequestSummary {
  return {
    ...summary,
    fromUserPhotoUrl: resolvePhotoUrl(summary.fromUserPhotoUrl, eventIdPublic),
    toUserPhotoUrl: resolvePhotoUrl(summary.toUserPhotoUrl, eventIdPublic),
  };
}

interface ConnectionStatusResult {
  readonly connectionStatus: DirectoryProfile["connectionStatus"];
  readonly pendingRequestIdPublic: ConnectionRequestIdPublic | null;
  readonly requestCreatedAt: Date | null;
  readonly connectedAt: Date | null;
}

// BOC-574 privacy model: priority connected > inbound > ignored > requested > none.
// DECLINED sent requests still show as "requested" so the requester can't infer
// whether the target ignored or expired the ask.
function determineConnectionStatus(
  sentToTarget: ConnectionRequestSummary | undefined,
  receivedFromTarget: ConnectionRequestSummary | undefined,
): ConnectionStatusResult {
  if (sentToTarget?.typeData.status === IntroRequestStatus.ACCEPTED) {
    return {
      connectionStatus: "connected",
      pendingRequestIdPublic: sentToTarget.idPublic,
      requestCreatedAt: null,
      connectedAt:
        sentToTarget.acceptedAt != null
          ? new Date(sentToTarget.acceptedAt)
          : null,
    };
  }
  if (receivedFromTarget?.typeData.status === IntroRequestStatus.ACCEPTED) {
    return {
      connectionStatus: "connected",
      pendingRequestIdPublic: receivedFromTarget.idPublic,
      requestCreatedAt: null,
      connectedAt:
        receivedFromTarget.acceptedAt != null
          ? new Date(receivedFromTarget.acceptedAt)
          : null,
    };
  }

  const activeStatuses: readonly string[] = IntroRequestStatus.ACTIVE_STATES;
  if (
    receivedFromTarget !== undefined &&
    activeStatuses.includes(receivedFromTarget.typeData.status)
  ) {
    return {
      connectionStatus: "inbound",
      pendingRequestIdPublic: receivedFromTarget.idPublic,
      requestCreatedAt: new Date(receivedFromTarget.createdAt),
      connectedAt: null,
    };
  }

  if (
    receivedFromTarget !== undefined &&
    receivedFromTarget.typeData.status === IntroRequestStatus.DECLINED
  ) {
    return {
      connectionStatus: "ignored",
      pendingRequestIdPublic: receivedFromTarget.idPublic,
      requestCreatedAt: null,
      connectedAt: null,
    };
  }

  // Sent + not CANCELLED → "requested". CANCELLED is the only outbound terminal
  // status that lets the requester see the truth (they cancelled it themselves).
  if (
    sentToTarget !== undefined &&
    sentToTarget.typeData.status !== IntroRequestStatus.CANCELLED
  ) {
    return {
      connectionStatus: "requested",
      pendingRequestIdPublic: sentToTarget.idPublic,
      requestCreatedAt: null,
      connectedAt: null,
    };
  }

  return {
    connectionStatus: "none",
    pendingRequestIdPublic: null,
    requestCreatedAt: null,
    connectedAt: null,
  };
}

// Composes the profile-page aggregate: the participant's directory card plus
// the connection state derived from the viewer's intro requests with them.
export function buildDirectoryProfile(
  raw: GetDirectoryProfileRawData,
  participantIdPublic: EventParticipantIdPublic,
): DirectoryProfile {
  if (raw.participantDto === undefined) {
    throw new ParticipantNotFoundError(participantIdPublic);
  }

  const entry = directoryEntryToCard(raw.participantDto);

  const sentToTarget = raw.introsItems.find(
    (req) => req.toParticipantIdPublic === participantIdPublic,
  );
  const receivedFromTarget = raw.introsItems.find(
    (req) => req.fromParticipantIdPublic === participantIdPublic,
  );

  const {
    connectionStatus,
    pendingRequestIdPublic,
    requestCreatedAt,
    connectedAt,
  } = determineConnectionStatus(sentToTarget, receivedFromTarget);

  return {
    entry,
    connectionStatus,
    pendingRequestIdPublic,
    requestCreatedAt,
    connectedAt,
  };
}
